// src/components/ProductDetail.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, X, Tag, Maximize2 } from 'lucide-react';
import { getCookie } from '../utils/utils';
import CreateProjectModal from './CreateProjectModal';
import { useCaps } from '../contexts/CapabilitiesContext';

interface Product {
  id: number;
  name: string;
  short_description: string;
  long_description: string;
  price: string;
  currency: string;
  converted_price?: number | null;
  converted_currency?: string | null;
  converted_currency_symbol?: string | null;
  duration_in_days: number;
  core_outputs_json: Array<{ name: string; path: string; image_url?: string }>;
  product_category: number;
  product_category_name?: string;
  product_category_workspace_key?: string;
  product_category_type?: string;
  product_category_type_color?: string;
  service_type?: 'DIY' | 'AI' | 'AI Off-Platform' | 'Catalyx';
  has_ai: boolean;
  is_free: boolean;
  created_at: string;
}

interface ProjectBlueprintOption {
  id: number;
  name: string;
  job_to_be_done?: string;
  tension?: string;
  market?: string;
}

interface SimpleProject {
  id: number;
  name: string;
}

interface ProductDetailProps {
  productId: number;
  onBack: () => void;
  selectedCurrency?: string;
  activeProject?: number | null;
  onProjectCreated?: () => void; // Callback to trigger sidebar refresh
}

const ProductDetail: React.FC<ProductDetailProps> = ({ productId, onBack, selectedCurrency = 'CHF', activeProject = null, onProjectCreated }) => {
  const { can } = useCaps();
  const canAiGenerate = can('ai_generate');
  const canMarketplaceOrder = can('marketplace_order');
  const canCreateProject = can('projects_create');
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOutputIndex, setSelectedOutputIndex] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [projectDetails, setProjectDetails] = useState('');
  const [contactName, setContactName] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [objective, setObjective] = useState('');
  const [markets, setMarkets] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFullScreenImage, setShowFullScreenImage] = useState(false);
  const [fullScreenImageIndex, setFullScreenImageIndex] = useState(0);

  // Project selector state for order form
  const [userProjects, setUserProjects] = useState<SimpleProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(activeProject);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [aiJobToBeDone, setAiJobToBeDone] = useState('');
  const [aiTargetBlueprintId, setAiTargetBlueprintId] = useState<number | null>(null);
  const [projectBlueprints, setProjectBlueprints] = useState<ProjectBlueprintOption[]>([]);

  const fetchProduct = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Don't pass currency param if it's CHF (default) - backend will use user's preference
      const url = `/api/products/${productId}/${selectedCurrency && selectedCurrency !== 'CHF' ? `?currency=${selectedCurrency}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProduct(data);
      } else {
        setError('Failed to load product');
      }
    } catch (err) {
      setError('An error occurred while loading product');
      console.error('Product fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [productId, selectedCurrency]);

  useEffect(() => {
    fetchProduct();
  }, [productId, selectedCurrency, fetchProduct]);

  // Sync selectedProjectId when activeProject changes
  useEffect(() => {
    if (activeProject !== null) {
      setSelectedProjectId(activeProject);
    }
  }, [activeProject]);

  // Fetch user's projects when modal opens
  useEffect(() => {
    if (!showContactModal || !selectedProjectId || product?.service_type !== 'AI') {
      setProjectBlueprints([]);
      return;
    }
    if (product.product_category_workspace_key !== 'recipe_card_builder') {
      setProjectBlueprints([]);
      return;
    }
    const loadBlueprints = async () => {
      try {
        const response = await fetch(`/api/blueprints/?project=${selectedProjectId}`, {
          credentials: 'include',
        });
        if (!response.ok) return;
        const data = await response.json();
        const rows = Array.isArray(data) ? data : data.results || [];
        setProjectBlueprints(
          rows.map((row: ProjectBlueprintOption) => ({
            id: row.id,
            name: row.name || `Platform #${row.id}`,
            job_to_be_done: row.job_to_be_done,
            tension: row.tension,
            market: row.market,
          }))
        );
      } catch {
        setProjectBlueprints([]);
      }
    };
    void loadBlueprints();
  }, [showContactModal, selectedProjectId, product?.service_type, product?.product_category_workspace_key]);

  useEffect(() => {
    if (showContactModal) {
      fetchUserProjects();
      if (activeProject !== null) {
        setSelectedProjectId(activeProject);
      }
    }
  }, [showContactModal, activeProject]);

  const fetchUserProjects = async () => {
    try {
      const response = await fetch('/api/projects/', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const projects = Array.isArray(data) ? data : data.results || [];
        setUserProjects(projects.map((p: any) => ({ id: p.id, name: p.name })));
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const handleProjectCreatedInOrder = (project: any) => {
    setUserProjects(prev => [{ id: project.id, name: project.name }, ...prev]);
    setSelectedProjectId(project.id);
    setShowCreateProjectModal(false);
    // Trigger sidebar refresh
    if (onProjectCreated) {
      onProjectCreated();
    }
  };

  const handleProductSelect = (selectedProductId: number) => {
    setError(null);
    if (product?.service_type === 'AI' && !canAiGenerate) {
      setError('You do not have permission to order AI products');
    } else if (
      (product?.service_type === 'Catalyx' || product?.service_type === 'AI Off-Platform') &&
      !canMarketplaceOrder
    ) {
      setError('You do not have permission to order Catalyx marketplace products');
    }
    setShowContactModal(true);
  };

  const handleCloseModal = () => {
    setShowContactModal(false);
    setProjectDetails('');
    setContactName('');
    setCompany('');
    setJobTitle('');
    setObjective('');
    setMarkets('');
    setTargetAudience('');
    setAdditionalNotes('');
    setShowSuccess(false);
    setError(null);
  };

  const handleSendRequest = async () => {
    if (!product) return;
    if (product.service_type === 'AI' && !canAiGenerate) {
      setError('You do not have permission to order AI products');
      return;
    }
    if (
      (product.service_type === 'Catalyx' || product.service_type === 'AI Off-Platform') &&
      !canMarketplaceOrder
    ) {
      setError('You do not have permission to order Catalyx marketplace products');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const csrfToken = getCookie('csrftoken');
      
      const response = await fetch('/api/orders/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify({
          product: product.id,
          quantity: 1,
          additional_info: projectDetails,
          contact_name: contactName,
          company: company,
          job_title: jobTitle,
          objective: objective,
          markets: markets,
          target_audience: targetAudience,
          additional_notes: additionalNotes,
          user_selected_currency: selectedCurrency,
          project: selectedProjectId || null,
          ai_target_blueprint_id: aiTargetBlueprintId,
          ai_job_to_be_done: aiJobToBeDone,
          ai_market: markets.split(',')[0]?.trim() || '',
        }),
      });

      if (response.ok) {
        const order = await response.json();
        console.log('Order created successfully:', order);
        
        // Write HubSpot logic here
        // This is where my colleague should implement HubSpot integration
        // Example: Send order data to HubSpot API, create deal, update contact, etc.
        
        // Show success message
        setShowSuccess(true);
        setProjectDetails('');
        setContactName('');
        setCompany('');
        setJobTitle('');
        setObjective('');
        setMarkets('');
        setTargetAudience('');
        setAdditionalNotes('');
        setError(null);
        
        // Auto-close modal after 3 seconds
        setTimeout(() => {
          setShowContactModal(false);
          setShowSuccess(false);
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || errorData.error || 'Failed to create order');
      }
    } catch (err) {
      setError('An error occurred while creating the order');
      console.error('Order creation error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (product: Product, duration: number, isFree: boolean) => {
    if (product.service_type === 'DIY') {
      return 'Included';
    }
    if (isFree) {
      return 'FREE';
    }
    
    // Use converted price if available, otherwise use original
    const displayPrice = product.converted_price ?? parseFloat(product.price);
    const displayCurrency = product.converted_currency_symbol ?? product.currency;
    
    if (displayPrice === 0) {
      return 'Price FREE';
    }
    return `Price from ${displayCurrency} ${displayPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} in ${duration} day${duration !== 1 ? 's' : ''}`;
  };

  // Get category color from API, with fallback to default
  const getCategoryColor = (product: Product | null): string => {
    if (product?.product_category_type_color) {
      return product.product_category_type_color;
    }
    // Fallback to default color if not set in database
    return '#0b3d91';
  };

  // Darken hex color for hover effects
  const darkenColor = (hex: string, percent: number = 15) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, ((num >> 16) & 0xff) - percent);
    const g = Math.max(0, ((num >> 8) & 0xff) - percent);
    const b = Math.max(0, (num & 0xff) - percent);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const categoryColor = getCategoryColor(product);
  const categoryColorHover = darkenColor(categoryColor, 20);

  const outputs = product?.core_outputs_json || [];
  const currentOutput = outputs.length > 0 ? outputs[selectedOutputIndex] : null;
  
  // Reset index if it's out of bounds
  useEffect(() => {
    if (outputs.length > 0 && selectedOutputIndex >= outputs.length) {
      setSelectedOutputIndex(0);
    }
  }, [outputs.length, selectedOutputIndex]);

  const handleNextOutput = () => {
    if (outputs.length > 0) {
      setSelectedOutputIndex((prev) => (prev + 1) % outputs.length);
    }
  };

  const handlePrevOutput = () => {
    if (outputs.length > 0) {
      setSelectedOutputIndex((prev) => (prev - 1 + outputs.length) % outputs.length);
    }
  };

  const handleOpenFullScreen = (index: number) => {
    setFullScreenImageIndex(index);
    setShowFullScreenImage(true);
  };

  const handleCloseFullScreen = () => {
    setShowFullScreenImage(false);
  };

  const handleFullScreenNext = () => {
    if (outputs.length > 0) {
      setFullScreenImageIndex((prev) => (prev + 1) % outputs.length);
    }
  };

  const handleFullScreenPrev = () => {
    if (outputs.length > 0) {
      setFullScreenImageIndex((prev) => (prev - 1 + outputs.length) % outputs.length);
    }
  };

  // Handle keyboard navigation in full screen
  useEffect(() => {
    if (!showFullScreenImage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowFullScreenImage(false);
      } else if (e.key === 'ArrowLeft' && outputs.length > 0) {
        setFullScreenImageIndex((prev) => (prev - 1 + outputs.length) % outputs.length);
      } else if (e.key === 'ArrowRight' && outputs.length > 0) {
        setFullScreenImageIndex((prev) => (prev + 1) % outputs.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFullScreenImage, outputs.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600">{error || 'Product not found'}</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-auto">
      {/* Header with Back Button */}
      <div className="border-b border-gray-200 h-[80px] 2xl:h-[100px] flex items-center px-8 2xl:px-12 bg-white/80 backdrop-blur-sm shadow-sm relative">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 2xl:w-6 2xl:h-6" />
          <span className="text-sm 2xl:text-base">Back</span>
        </button>
      </div>

      <div className="flex gap-10 2xl:gap-12 px-8 2xl:px-12 pt-8 2xl:pt-10 pb-16 2xl:pb-20 max-w-[1400px] 2xl:max-w-[1600px] mx-auto">
        {/* Left Column - Product Info & Outputs */}
        <div className="flex-1 max-w-[800px]">
          {/* Product Title */}
          <div className="mb-8 2xl:mb-10">
            <h1 className="text-2xl 2xl:text-3xl font-bold text-black mb-4 2xl:mb-5 normal-case">
              {product.name}
            </h1>
            {product.product_category_name && (
              <span 
                className="inline-block px-4 2xl:px-5 py-2 2xl:py-2.5 text-sm 2xl:text-base font-medium rounded-full"
                style={{ 
                  backgroundColor: `${categoryColor}20`,
                  color: categoryColor
                }}
              >
                {product.product_category_name}
              </span>
            )}
          </div>

          {/* Product Description */}
          <div className="mb-12 2xl:mb-16 bg-white/60 backdrop-blur-sm rounded-lg p-8 2xl:p-10 shadow-sm border border-gray-100">
            <p className="text-base 2xl:text-lg text-gray-700 leading-7 2xl:leading-8 whitespace-pre-wrap">
              {product.long_description || product.short_description}
            </p>
          </div>

          {/* Outputs Section - Only show if outputs exist */}
          {outputs.length > 0 && (
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-8 2xl:p-10 shadow-sm border border-gray-100">
            <h2 className="text-2xl 2xl:text-3xl font-semibold text-black mb-8 2xl:mb-10 flex items-center gap-3 2xl:gap-4">
              <div 
                className="w-1.5 h-8 2xl:w-2 2xl:h-10 rounded-full"
                style={{ backgroundColor: categoryColor }}
              ></div>
              Outputs
            </h2>

            {/* Main Output Display */}
            {currentOutput ? (
              <div className={`${currentOutput.image_url ? 'bg-white' : 'bg-gradient-to-br from-neutral-100 to-neutral-200'} h-[400px] 2xl:h-[480px] w-full mb-8 2xl:mb-10 rounded-lg flex flex-col items-center justify-center overflow-hidden p-6 2xl:p-8 relative group shadow-inner`}>
                {/* Display image if image_url is available, otherwise show path as text */}
                {currentOutput.image_url ? (
                  <>
                    <img
                      src={currentOutput.image_url}
                      alt={currentOutput.name || 'Output image'}
                      className="w-full h-full object-contain cursor-pointer"
                      onClick={() => handleOpenFullScreen(selectedOutputIndex)}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        // Fallback to showing path if image fails to load
                        const parent = target.parentElement;
                        if (parent && currentOutput.path) {
                          parent.innerHTML = `<div class="text-center"><p class="text-base text-black break-all">${currentOutput.path}</p></div>`;
                          parent.className = 'bg-gradient-to-br from-neutral-100 to-neutral-200 h-[400px] w-full mb-8 rounded-lg flex flex-col items-center justify-center overflow-hidden p-6 relative group shadow-inner';
                        } else {
                          target.style.display = 'none';
                        }
                      }}
                    />
                    {/* Full screen button overlay */}
                    <button
                      onClick={() => handleOpenFullScreen(selectedOutputIndex)}
                      className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-3 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                      title="View full screen"
                    >
                      <Maximize2 className="w-5 h-5" />
                    </button>
                  </>
                ) : currentOutput.path ? (
                  <div className="text-center">
                    <p className="text-base text-black break-all">
                      {currentOutput.path}
                    </p>
                  </div>
                ) : (
                  <span className="text-base text-black">No output available</span>
                )}
              </div>
            ) : (
              <div className="bg-neutral-200 h-[400px] w-full mb-6 flex items-center justify-center">
                <span className="text-base text-black">No outputs available</span>
              </div>
            )}

            {/* Output Name and Navigation - Always show if outputs exist */}
            {outputs.length > 0 && currentOutput && (
              <>
                {/* Output Name - Directly under the image, constrained to 600px */}
                <div className="mb-6 2xl:mb-7 max-w-[800px] 2xl:max-w-[900px]">
                  <p
                    className="text-lg 2xl:text-xl font-semibold break-words text-black"
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {currentOutput.name || 'Output'}
                  </p>
                </div>

                {/* Navigation Arrows - Below the name */}
                <div className="flex items-center justify-start gap-4 2xl:gap-5 mb-8 2xl:mb-10">
                  <button
                    onClick={handlePrevOutput}
                    className="w-7 h-7 2xl:w-8 2xl:h-8 flex items-center justify-center hover:opacity-70 transition-opacity disabled:opacity-30 flex-shrink-0"
                    disabled={outputs.length <= 1}
                  >
                    <ChevronLeft 
                      className="w-6 h-6 2xl:w-7 2xl:h-7" 
                      style={{ color: categoryColor }}
                    />
                  </button>
                  <span 
                    className="text-sm 2xl:text-base whitespace-nowrap"
                    style={{ color: categoryColor }}
                  >
                    {selectedOutputIndex + 1} / {outputs.length}
                  </span>
                  <button
                    onClick={handleNextOutput}
                    className="w-7 h-7 2xl:w-8 2xl:h-8 flex items-center justify-center hover:opacity-70 transition-opacity disabled:opacity-30 flex-shrink-0"
                    disabled={outputs.length <= 1}
                  >
                    <ChevronRight 
                      className="w-6 h-6 2xl:w-7 2xl:h-7" 
                      style={{ color: categoryColor }}
                    />
                  </button>
                </div>
              </>
            )}

            {/* Thumbnail Grid - Show if more than 1 output */}
            {outputs.length > 1 && (
              <div className="grid grid-cols-4 gap-5 2xl:gap-6 mt-8 2xl:mt-10">
                {outputs.slice(0, 8).map((output, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedOutputIndex(index)}
                    className={`${output.image_url ? 'bg-white' : 'bg-neutral-200'} h-[130px] 2xl:h-[150px] w-[130px] 2xl:w-[150px] overflow-hidden rounded-lg flex flex-col items-center justify-center p-3 2xl:p-4 hover:opacity-80 transition-opacity`}
                    style={selectedOutputIndex === index ? { 
                      boxShadow: `0 0 0 3px ${categoryColor}`
                    } : {}}
                  >
                    {output.image_url ? (
                      <img
                        src={output.image_url}
                        alt={output.name || 'Output thumbnail'}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => handleOpenFullScreen(index)}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          // Fallback to showing path if image fails to load
                          const parent = target.parentElement;
                          if (parent && output.path) {
                            parent.innerHTML = `<p class="text-sm text-black text-center break-all line-clamp-3">${output.path}</p>`;
                            parent.className = `bg-neutral-200 h-[130px] w-[130px] overflow-hidden rounded-lg flex flex-col items-center justify-center p-3 hover:opacity-80 transition-opacity`;
                            if (selectedOutputIndex === index) {
                              parent.style.boxShadow = `0 0 0 3px ${categoryColor}`;
                            }
                          } else {
                            target.style.display = 'none';
                          }
                        }}
                      />
                    ) : output.path ? (
                      <p className="text-sm text-black text-center break-all line-clamp-3">
                        {output.path}
                      </p>
                    ) : (
                      <span className="text-sm text-gray-500">No URL</span>
                    )}
                    {output.name && !output.image_url && (
                      <p className="text-xs text-gray-600 mt-1 text-center line-clamp-1">
                        {output.name}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
        </div>

        {/* Right Column - Product Info & Related */}
        <div className="w-[480px] 2xl:w-[540px] flex flex-col gap-8 2xl:gap-10 flex-shrink-0 sticky top-24 2xl:top-28 self-start">
          {/* Current Product Info Card */}
          <div 
            className="bg-white border-2 p-8 2xl:p-10 rounded-xl shadow-lg"
            style={{ borderColor: categoryColor }}
          >
            <div className="mb-6 2xl:mb-8">
              <p className="text-xl 2xl:text-2xl font-semibold text-black mb-4 2xl:mb-5">
                {product.name}
              </p>
              {/* Price */}
              <div className="flex items-baseline gap-2 mb-5 2xl:mb-6">
                <p 
                  className="text-lg 2xl:text-xl font-bold"
                  style={{ color: categoryColor }}
                >
                  {formatPrice(product, product.duration_in_days, product.is_free)}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleProductSelect(product.id)}
              disabled={false}
              title={
                product.service_type === 'AI' && !canAiGenerate
                  ? 'You do not have permission to order AI products'
                  : (product.service_type === 'Catalyx' || product.service_type === 'AI Off-Platform') &&
                      !canMarketplaceOrder
                    ? 'You do not have permission to order Catalyx marketplace products'
                    : undefined
              }
              className="text-white px-6 2xl:px-8 py-4 2xl:py-5 rounded-lg shadow-lg w-full text-lg 2xl:text-xl font-semibold transition-all duration-200 hover:shadow-xl hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{ 
                backgroundColor: categoryColor,
              }}
              onMouseEnter={(e) => {
                if (e.currentTarget.disabled) return;
                e.currentTarget.style.backgroundColor = categoryColorHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = categoryColor;
              }}
            >
              Buy
            </button>
          </div>

          {/* Catalyx AI Card - Show if product has_ai is true */}
          {product.has_ai && (
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 p-8 2xl:p-10 relative rounded-xl shadow-lg">
              <div className="absolute bg-indigo-100 px-4 2xl:px-5 py-2 2xl:py-2.5 rounded-full top-5 2xl:top-6 right-5 2xl:right-6 shadow-sm">
                <p className="text-sm 2xl:text-base font-medium text-indigo-700">Coming soon</p>
              </div>
              <div className="mb-5 2xl:mb-6 pr-24 2xl:pr-28">
                <p className="text-xl 2xl:text-2xl font-semibold text-black mb-4 2xl:mb-5">
                  Catalyx AI
                </p>
                <p className="text-base 2xl:text-lg text-gray-700 leading-7 2xl:leading-8">
                  &lt;this is the description of the Catalyx AI offering&gt;
                </p>
              </div>
              <button
                onClick={() => handleProductSelect(product.id)}
                className="bg-white border-2 border-indigo-300 text-indigo-700 px-6 2xl:px-8 py-4 2xl:py-5 rounded-lg w-full text-base 2xl:text-lg font-semibold hover:bg-indigo-50 hover:border-indigo-400 transition-all duration-200 hover:shadow-md"
              >
                Request Early Access
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && product && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-scaleIn">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div 
                  className="w-5 h-5 rounded flex items-center justify-center"
                  style={{ backgroundColor: categoryColor }}
                >
                  <Tag className="w-4 h-4 text-white" />
                </div>
                <p 
                  className="text-base font-medium"
                  style={{ color: categoryColor }}
                >
                  {product.name}
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="px-6 py-5 overflow-y-auto flex-1">
              {showSuccess ? (
                /* Success Message */
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-black mb-2">
                    Request Sent!
                  </h2>
                  <p className="text-sm text-gray-600">
                    Catalyx received your request. We will get back to you!
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-black mb-2">
                    Get in touch
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    Tell us about your project and we'll get in touch to start your {product.name} work immediately.
                  </p>

                  {/* Error Message */}
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  {/* Project Selector */}
                  <div className="mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project *
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={selectedProjectId ?? ''}
                        onChange={(e) => setSelectedProjectId(e.target.value ? parseInt(e.target.value) : null)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm bg-white"
                      >
                        <option value="">Select a project...</option>
                        {userProjects.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                      {canCreateProject && (
                        <button
                          type="button"
                          onClick={() => setShowCreateProjectModal(true)}
                          className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                        >
                          + New
                        </button>
                      )}
                    </div>
                    {!selectedProjectId && (
                      <p className="mt-1.5 text-xs text-amber-600">
                        A project is required to send a request.
                      </p>
                    )}
                  </div>

                  {product.service_type === 'AI' &&
                    product.product_category_workspace_key === 'tension_identifier' && (
                      <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Job To Be Done *
                        </label>
                        <input
                          type="text"
                          value={aiJobToBeDone}
                          onChange={(e) => setAiJobToBeDone(e.target.value)}
                          placeholder="Enter the job this tension analysis is for"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm"
                        />
                      </div>
                    )}

                  {product.service_type === 'AI' &&
                    product.product_category_workspace_key === 'recipe_card_builder' && (
                      <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Innovation Platform *
                        </label>
                        <select
                          value={aiTargetBlueprintId ?? ''}
                          onChange={(e) =>
                            setAiTargetBlueprintId(
                              e.target.value ? parseInt(e.target.value, 10) : null
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm bg-white"
                          disabled={!selectedProjectId}
                        >
                          <option value="">Select an innovation platform...</option>
                          {projectBlueprints.map((blueprint) => (
                            <option key={blueprint.id} value={blueprint.id}>
                              {blueprint.job_to_be_done || blueprint.name}
                              {blueprint.tension ? ` / ${blueprint.tension}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                  {/* Form Fields */}
                  <div className="space-y-4">
                    {/* Row 1: Name and Company */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Your name
                        </label>
                        <input
                          type="text"
                          value={contactName}
                          onChange={(e) => setContactName(e.target.value)}
                          placeholder="Enter your name"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none text-sm"
                          style={{
                            '--focus-ring-color': categoryColor
                          } as React.CSSProperties & { '--focus-ring-color': string }}
                          onFocus={(e) => {
                            e.currentTarget.style.boxShadow = `0 0 0 2px ${categoryColor}`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.boxShadow = '';
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Your company
                        </label>
                        <input
                          type="text"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          placeholder="Enter your company name"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none text-sm"
                          style={{
                            '--focus-ring-color': categoryColor
                          } as React.CSSProperties & { '--focus-ring-color': string }}
                          onFocus={(e) => {
                            e.currentTarget.style.boxShadow = `0 0 0 2px ${categoryColor}`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.boxShadow = '';
                          }}
                        />
                      </div>
                    </div>

                    {/* Row 2: Job Title and Markets */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Your job title
                        </label>
                        <input
                          type="text"
                          value={jobTitle}
                          onChange={(e) => setJobTitle(e.target.value)}
                          placeholder="Enter your job title"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none text-sm"
                          style={{
                            '--focus-ring-color': categoryColor
                          } as React.CSSProperties & { '--focus-ring-color': string }}
                          onFocus={(e) => {
                            e.currentTarget.style.boxShadow = `0 0 0 2px ${categoryColor}`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.boxShadow = '';
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Markets
                        </label>
                        <input
                          type="text"
                          value={markets}
                          onChange={(e) => setMarkets(e.target.value)}
                          placeholder="Enter target markets"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none text-sm"
                          style={{
                            '--focus-ring-color': categoryColor
                          } as React.CSSProperties & { '--focus-ring-color': string }}
                          onFocus={(e) => {
                            e.currentTarget.style.boxShadow = `0 0 0 2px ${categoryColor}`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.boxShadow = '';
                          }}
                        />
                      </div>
                    </div>

                    {/* Objective */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Your objective/ end goal in 1-2 sentences
                      </label>
                      <textarea
                        value={objective}
                        onChange={(e) => setObjective(e.target.value)}
                        placeholder="Describe your objective or end goal"
                        rows={2}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none resize-none text-sm"
                        style={{
                          '--focus-ring-color': categoryColor
                        } as React.CSSProperties & { '--focus-ring-color': string }}
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow = `0 0 0 2px ${categoryColor}`;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = '';
                        }}
                      />
                    </div>

                    {/* Target Audience */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Target Audience <span className="text-gray-500 font-normal">(e.g. male/ female/ age/ category buyers/ rejectors etc.)</span>
                      </label>
                      <textarea
                        value={targetAudience}
                        onChange={(e) => setTargetAudience(e.target.value)}
                        placeholder="Describe your target audience"
                        rows={2}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none resize-none text-sm"
                        style={{
                          '--focus-ring-color': categoryColor
                        } as React.CSSProperties & { '--focus-ring-color': string }}
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow = `0 0 0 2px ${categoryColor}`;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = '';
                        }}
                      />
                    </div>

                    {/* Additional Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Anything else that will help us
                      </label>
                      <textarea
                        value={additionalNotes}
                        onChange={(e) => setAdditionalNotes(e.target.value)}
                        placeholder="Any additional information"
                        rows={2}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none resize-none text-sm"
                        style={{
                          '--focus-ring-color': categoryColor
                        } as React.CSSProperties & { '--focus-ring-color': string }}
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow = `0 0 0 2px ${categoryColor}`;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = '';
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer Buttons */}
            {!showSuccess && (
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendRequest}
                  disabled={
                    isSubmitting ||
                    !contactName.trim() ||
                    !selectedProjectId ||
                    ((product.service_type === 'Catalyx' || product.service_type === 'AI Off-Platform') &&
                      !canMarketplaceOrder) ||
                    (product.service_type === 'AI' && !canAiGenerate) ||
                    (product.service_type === 'AI' &&
                      product.product_category_workspace_key === 'tension_identifier' &&
                      !aiJobToBeDone.trim()) ||
                    (product.service_type === 'AI' &&
                      product.product_category_workspace_key === 'recipe_card_builder' &&
                      !aiTargetBlueprintId)
                  }
                  className="px-6 py-2.5 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
                  style={{ 
                    backgroundColor: categoryColor,
                  }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor = categoryColorHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = categoryColor;
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Project Modal (from order form) */}
      {showCreateProjectModal && (
        <CreateProjectModal
          isOpen={showCreateProjectModal}
          onClose={() => setShowCreateProjectModal(false)}
          onProjectCreated={handleProjectCreatedInOrder}
        />
      )}

      {/* Full Screen Image Modal */}
      {showFullScreenImage && outputs.length > 0 && outputs[fullScreenImageIndex]?.image_url && (
        <div 
          className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={handleCloseFullScreen}
        >
          {/* Close Button */}
          <button
            onClick={handleCloseFullScreen}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 hover:bg-black/70 p-2 rounded-md"
            title="Close (ESC)"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation Buttons */}
          {outputs.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFullScreenPrev();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 hover:bg-black/70 p-3 rounded-md"
                title="Previous (←)"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFullScreenNext();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 hover:bg-black/70 p-3 rounded-md"
                title="Next (→)"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          {/* Image Counter */}
          {outputs.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-md text-sm">
              {fullScreenImageIndex + 1} / {outputs.length}
            </div>
          )}

          {/* Full Screen Image */}
          <div 
            className="max-w-full max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={outputs[fullScreenImageIndex].image_url}
              alt={outputs[fullScreenImageIndex].name || 'Full screen image'}
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>

          {/* Image Name */}
          {outputs[fullScreenImageIndex].name && (
            <div
              className="absolute bottom-16 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-md text-sm max-w-md text-center"
              style={{ whiteSpace: 'pre-line' }}
            >
              {outputs[fullScreenImageIndex].name}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
